import { Link } from 'react-router-dom'
import { CheckCircle2, FlaskConical, Loader2, AlertTriangle, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ResearchProgress } from './use-research'

// M5-A: User-facing Labels ohne Modul-/Modellnamen. Die technischen Phasen
// (grok = Social-Media-Suche, synthesize = LLM-Zusammenfassung) bleiben im Code.
const PHASE_LABEL: Record<string, string> = {
  queued: 'Bereitstellung',
  web: 'Websuche läuft',
  academic: 'Akademische Quellen',
  grok: 'Soziale Medien',
  youtube: 'YouTube',
  podcasts: 'Podcasts',
  legal: 'Rechtliche Quellen',
  sources_loaded: 'Quellen geladen',
  synthesize: 'Zusammenfassung wird erstellt',
  write_sources: 'Quellen werden gespeichert',
  write_dossier: 'Dossier wird erstellt',
  done: 'Fertig',
  error: 'Fehler',
}

interface ResearchBubbleProps {
  state: ResearchProgress
  onDismiss?: () => void
}

export function ResearchBubble({ state, onDismiss }: ResearchBubbleProps) {
  const isDone = state.phase === 'done'
  const isError = state.phase === 'error'
  const phases = ['web', 'academic', 'grok', 'youtube', 'podcasts', 'legal'] as const

  const dossierLink = state.result?.dossier_path
    ? `/vault?path=${encodeURIComponent(state.result.dossier_path)}`
    : null

  return (
    <div className="flex w-full justify-start">
      <div className="min-w-0 max-w-[80%] rounded-lg border border-border bg-card text-card-foreground">
        <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          {isDone ? (
            <CheckCircle2 className="h-4 w-4 text-success" />
          ) : isError ? (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          ) : (
            <FlaskConical className="h-4 w-4 animate-pulse text-primary" />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">Research: {state.topic}</div>
            <div className="text-[12px] text-muted-foreground">
              {isError ? state.error : (PHASE_LABEL[state.phase] ?? state.phase)}
              {state.detail ? ` · ${state.detail}` : ''}
            </div>
          </div>
          {(isDone || isError) && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="min-h-11 shrink-0 text-[12px] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:min-h-9"
            >
              schliessen
            </button>
          )}
        </header>

        {!isDone && !isError && (
          <div className="px-4 py-3">
            <ul className="grid grid-cols-1 gap-1.5 text-[13px] sm:grid-cols-2">
              {phases.map((p) => {
                const prog = state.progressByPhase[p]
                if (!prog) return null
                return (
                  <li key={p} className="flex min-w-0 items-center gap-2 text-muted-foreground">
                    {prog.done >= prog.total ? (
                      <CheckCircle2 className="h-3 w-3 shrink-0 text-success" />
                    ) : (
                      <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                    )}
                    <span className="min-w-0 truncate font-mono">{PHASE_LABEL[p] ?? p}</span>
                    <span className="ml-auto shrink-0 text-[12px]">
                      {prog.done}/{prog.total}
                    </span>
                  </li>
                )
              })}
            </ul>
            {state.sources.length > 0 && (
              <div className="mt-3 text-[12px] text-muted-foreground">
                {state.sources.length} Quellen geladen
              </div>
            )}
          </div>
        )}

        {isDone && state.result && (
          <div className="space-y-3 px-4 py-3">
            {/* Tier-Counts (tier_1/2/3) bleiben im Result, werden aber nicht
                mehr als Jargon im UI gezeigt (M5-A). */}
            <div className="text-[12px] text-muted-foreground">
              {state.sources.length} Quellen
            </div>

            {state.result.key_findings.length > 0 && (
              <div>
                <div className="mb-1.5 text-[12px] font-medium text-muted-foreground">
                  Wichtigste Erkenntnisse
                </div>
                <ul className="space-y-1.5 text-sm">
                  {state.result.key_findings.slice(0, 3).map((f, i) => (
                    <li key={i} className="line-clamp-2 leading-relaxed">
                      • {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {dossierLink && (
              <Link
                to={dossierLink}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90',
                )}
              >
                <FileText className="h-3.5 w-3.5" />
                Dossier öffnen
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
